import { isNil } from "lodash-es";
import ReconnectingWebSocket from "reconnecting-websocket";

import {
  PUBSUB_VIRTUAL_CLASS_STATE_MESSAGE_TYPE,
  type PubsubVirtualClassStateMessageFromServerType,
} from "../shared/types/av-state";
import type {
  ClientPubsubModelUpdateType,
  PubsubAuthUpdateType,
  PubsubMessageType,
  VersionUpdateType,
} from "../shared/types/pubsub";
import { type WebsocketErrorType } from "../shared/types/websocket-auth";
import { pageAuthorizationRemoved } from "../shared/utils";
import { type AppDispatch, slices, store } from "../store/index";
import { selectCourseVersion } from "../store/selectors";
import { attendanceStateActions } from "../store/slices/attendance";
import { authStateActions } from "../store/slices/auth";
import { viewStateActions } from "../store/slices/view";

import { BROADCAST_TOPICS, sendBroadcast } from "./broadcast";
import { isMoreThanXMinutesAgo } from "./date";
import { logInfoWithDebugFlag, rollbarAndLogError, rollbarAndLogWarning } from "./logger";
import { clearCacheAndReload } from "./urls";

import { handlePubsubVirtualClassStateMessage } from "components/av/virtual-class-utils";
import { localStateActions } from "store/slices/local";

// Keep this in sync with COURSE_MESSAGE_QUEUE_MAX_LENGTH from material/tasks--this
// is the most messages we'll store in our redis queue for missed pubsub messages.
const MAX_REDIS_COURSE_VERSION_DIFF = 500;

// This is used to track the last time we did a full refresh of the page.
// We use this to determine if we should reload the page due to a large version diff.
const LAST_FULL_REFRESH_DATE_KEY = "ForumPubsubLastFullRefreshDate";
const MIN_TIME_BETWEEN_FULL_REFRESHES_MIN = 5;

/**
 * Handles auth update messages
 */
export const handleAuthMessage = (message: PubsubAuthUpdateType, dispatch: AppDispatch) => {
  dispatch(authStateActions.setWebsocketUserData(message.data));

  // Remove the page from the store not just the pages in the websocket
  if (message.data.scope === "individual") {
    const worksheetId = Object.keys(message.data.pages)[0];
    const authPage = message.data.pages[worksheetId];

    if (pageAuthorizationRemoved(authPage)) {
      const state = store.getState();
      const page = Object.values(state.pages).find((p) => p.worksheet_id === worksheetId);

      if (page) {
        dispatch(slices.pages.actions.removeOne({ id: page.id }));
      }
    }
  }
};

/**
 * Handles model update messages
 */
export const handleModelMessage = (
  message: ClientPubsubModelUpdateType,
  courseId: string | null,
  dispatch: AppDispatch,
) => {
  const checkAndIncrementCourseVersion = () => {
    const { course_version: newCourseVersion, course_id: msgCourseId } = message;
    const courseVersion = selectCourseVersion(store.getState());

    if (
      !msgCourseId ||
      isNil(newCourseVersion) ||
      courseId !== msgCourseId ||
      newCourseVersion === courseVersion
    )
      return true;

    const versionDiff = newCourseVersion - courseVersion;
    // Only actually increment the course version if it's the next version
    // being applied
    if (versionDiff === 1) {
      dispatch(viewStateActions.setCourseVersionTo(newCourseVersion));
      return true;
    }

    if (versionDiff < 0) {
      // If we receive a version from an API call that is _lower_ than the current one,
      // it likely means that in between the server calculating the response it coming back to us,
      // we also received a pubsub message with a version increase. In this case,
      // we ignore the returned version value, since we've already correctly incremented beforehand.
      // We still set isCourseVersionOutOfSync to true, just to ensure we send a message to double-check
      // the version on the server. This should be a no-op, just a sanity check.
      logInfoWithDebugFlag(
        "debug_pubsub",
        "Received a version from an API call that is lower than the current one",
        {
          courseId,
          newCourseVersion,
          oldCourseVersion: courseVersion,
        },
      );
      // TODO: Will this do anything useful?
      dispatch(viewStateActions.setIsCourseVersionOutOfSync(true));
    } else if (versionDiff > MAX_REDIS_COURSE_VERSION_DIFF) {
      const lastFullRefreshDate = localStorage.getItem(LAST_FULL_REFRESH_DATE_KEY);

      if (
        !lastFullRefreshDate ||
        isMoreThanXMinutesAgo(lastFullRefreshDate, MIN_TIME_BETWEEN_FULL_REFRESHES_MIN)
      ) {
        // If we are way out of sync, reload the page to get the latest data
        rollbarAndLogWarning(`Reloading due to large version diff`, {
          courseId,
          newCourseVersion,
          oldCourseVersion: courseVersion,
        });
        localStorage.setItem(LAST_FULL_REFRESH_DATE_KEY, new Date().toISOString());

        // We could make this more subtle by clearing the store and only remounting
        // the root component to load all our data, but this shouldn't happen often
        // enough to warrant the complexity.  We can change if that's not the case.
        window.location.reload();
      } else {
        rollbarAndLogWarning(`Attempting to reload too frequently`, {
          courseId,
          newCourseVersion,
          oldCourseVersion: courseVersion,
          lastFullRefreshDate,
        });
      }
    } else {
      dispatch(viewStateActions.setIsCourseVersionOutOfSync(true));
    }

    return false;
  };

  const isValidMessage = checkAndIncrementCourseVersion();

  if (isValidMessage && "model" in message) {
    sendBroadcast(BROADCAST_TOPICS.PUBSUB_MODEL_UPDATE, message);
  }
};

/**
 * Sets up socket event listeners for pubsub messages
 */
export const setupSocketListeners = (
  socket: ReconnectingWebSocket,
  {
    courseId,
    userId,
    dispatch,
    onError,
  }: {
    courseId: string | null;
    userId: string | null;
    dispatch: AppDispatch;
    onError: (errorCode: "401" | null) => void;
  },
) => {
  socket.addEventListener("error", (error) => {
    const errorMessage = error?.message || error || "Unknown error";
    const state = (error?.target?.readyState || socket.readyState) as string;

    rollbarAndLogWarning("Websocket Error:", {
      timestamp: new Date().toISOString(),
      userId,
      courseId,
      errorMessage,
      state,
    });
  });

  socket.addEventListener("message", (evt: MessageEvent) => {
    try {
      if (typeof evt.data !== "string") {
        rollbarAndLogError("Socket got message evt.data of unexpected type", typeof evt.data);
        return;
      }

      const data = JSON.parse(evt.data) as
        | PubsubMessageType
        | PubsubVirtualClassStateMessageFromServerType
        | VersionUpdateType
        | { type: "error"; error: WebsocketErrorType };

      if (data?.type === PUBSUB_VIRTUAL_CLASS_STATE_MESSAGE_TYPE) {
        handlePubsubVirtualClassStateMessage(data, dispatch);
        return;
      }

      if (data?.type === "version-update") {
        const clientVersion = window._env_.COMMIT;
        const serverVersion = data.serverVersion;
        if (clientVersion === serverVersion) {
          dispatch(localStateActions.setShowVersionMismatchWarning(false));
          dispatch(localStateActions.setHasHandledVersionMismatch(false));
        } else {
          // Check if this version mismatch happened during initial page load
          const timeSinceLoad = performance.now();
          const isDuringInitialLoad = timeSinceLoad < 10000; // 10 seconds
          const state = store.getState();
          const hasHandledVersionMismatch = state.local.hasHandledVersionMismatch;

          if (isDuringInitialLoad && !hasHandledVersionMismatch) {
            // Page loaded outdated code → Auto-refresh with cache clear (only once)
            dispatch(localStateActions.setShowVersionMismatchWarning(false));
            dispatch(localStateActions.setHasHandledVersionMismatch(true));
            clearCacheAndReload();
          } else {
            // The page was already loaded when the version mismatch happened, or we already handled it
            dispatch(localStateActions.setShowVersionMismatchWarning(true));
            dispatch(localStateActions.setHasHandledVersionMismatch(true));
          }
        }
        return;
      }

      const messages = data?.type === "pubsub" ? data.messages : [];

      for (const message of messages) {
        if (message.type && message.type === "auth-update") {
          // Adding some additional logging to see why auth update are not being logged
          logInfoWithDebugFlag("debug_sharedb_all_messages", "Received auth message", {
            courseId,
            userId,
            messageType: data?.type,
            message: JSON.stringify(message).substring(0, 100),
          });

          handleAuthMessage(message, dispatch);
        } else if (message.type && message.type === "model-update") {
          handleModelMessage(message, courseId, dispatch);
        } else if (message.type && message.type === "attendance-update") {
          dispatch(attendanceStateActions.updateAnalyticsRecord(message));
        }
      }

      if ("error" in data && data.error) {
        const code = data.error.code;
        // All of these send rollbar messages on the backend, so we don't need to do that here
        if (code === 403) {
          // An operation was rejected due to missing permissions. This shouldn't happen,
          // but should also not be fatal. We leave it to ShareDBDoc to handle this.
          // TODO: We might want to briefly flash a small "error" connection indicator state once
          // we have the more subdued connection indicator design in place, just to give visual indications
          // for small things going wrong.
        } else if (code === 401) {
          // The JWT token is invalid. This is fatal, so we close the connection. We don't
          // log the user out forcefully to prevent login loops.
          onError("401");
        } else {
          // If the error is critical, ShareDB will re-try connecting for a bit, and if not
          // successful, it will close the connection, which consumers can react to by listening
          // to the sharedbStatus.
        }
      }
    } catch (err) {
      console.error("Error reading socket data", err, evt.data);
    }
  });
};
